/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useCallback } from 'react';
import { point } from '@turf/helpers';
import distance from '@turf/distance';
import bearing from '@turf/bearing';
import { VarName, GeoJSON } from '../../../services/api';
import { getMotionMap, MarkerData, SpaceParams } from '../mapHelpers';
import { SensorValueItem } from '../../CalendarView/helpers';
import { setClickedItem, setHighlightedItem } from '../../../state/actions';
import { ActiveItem } from '../../../state/types';
import getFloorplanLayer from './floorPlanLayer';
import { DeckGLViewPort } from '../types';
import getLabelLayer from './labelLayer';
import getTargetPosLayer from './targetPosLayer';
import getSummaryCoverageLayer from './summaryCoverageLayer';
import getSensorCoverageLayer from './sensorCoverageLayer';
import { useAppDispatch, useAppSelector } from '../../../state/store';
import {
  getActiveMarker,
  getClickedItem,
  getCurrentLocation,
  getCurrentLocationFloorplan,
  getHighlightedItem,
  getMotionThreshold,
  getSensorsById,
  getShowTaggedSpaces,
  getUserLocation,
  getUserPosition,
} from '../../../state/selectors';
import getOccSignaturesLayer from './occSignaturesLayer';
import getMotionPins from './motionPins';
import getAreaCoverageLayer from './areaCoverage';

export default function useFloorLayers(
  viewState: DeckGLViewPort,
  markerData: MarkerData[],
  overrideData?: SensorValueItem[],
  allSpaces?: SpaceParams[]
) {
  const dispatch = useAppDispatch();
  const currentLocation = useAppSelector(getCurrentLocation);
  const highlightedItem = useAppSelector(getHighlightedItem);
  const clickedItem = useAppSelector(getClickedItem);
  const motionThreshold = useAppSelector(getMotionThreshold);
  const userPosition = useAppSelector(getUserPosition);
  const userLocation = useAppSelector(getUserLocation);
  const activeMarker = useAppSelector(getActiveMarker);
  const floorPlan = useAppSelector(getCurrentLocationFloorplan);
  const sensorsById = useAppSelector(getSensorsById);
  const showTaggedSpaces = useAppSelector(getShowTaggedSpaces);

  const showSensorCoverage = useMemo(
    () => !(allSpaces && allSpaces.length > 0 && showTaggedSpaces),
    [allSpaces, showTaggedSpaces]
  );

  const deckGLLayers: any[] = [];

  const motionMap = useMemo(() => {
    const isUtlData = activeMarker === VarName.MotionEvent && overrideData !== undefined;
    return getMotionMap(markerData, isUtlData);
  }, [activeMarker, overrideData, markerData]);

  const onSensorHover = useCallback(
    (item: ActiveItem) => {
      dispatch(setHighlightedItem(item));
    },
    [dispatch]
  );

  const onSensorClick = useCallback(
    (item: ActiveItem) => {
      dispatch(setClickedItem(item));
    },
    [dispatch]
  );

  const showMotion = activeMarker === VarName.MotionEvent;
  const showOverrideData = overrideData !== undefined;

  const floorPlanLayer = useMemo(() => {
    if (!floorPlan) return null;

    return getFloorplanLayer(
      floorPlan,
      viewState.zoom,
      showMotion,
      highlightedItem.id ? highlightedItem : clickedItem,
      motionMap,
      motionThreshold,
      showOverrideData,
      onSensorHover,
      onSensorClick
    );
  }, [
    floorPlan,
    viewState.zoom,
    showMotion,
    highlightedItem,
    clickedItem,
    motionMap,
    motionThreshold,
    showOverrideData,
    onSensorHover,
    onSensorClick,
  ]);

  function getFloorplanAngle(floor: GeoJSON) {
    const feature = floor.features.find((item) => item.properties.type === 'area.floor');
    const coordinates = feature?.geometry?.coordinates[0][0]; // First polygon, first ring

    // method 1 (keeping, if needed for alternatives)
    // let maxLength = 0;
    // let angle = 0;
    // if (coordinates) {
    //   // Find the longest edge to determine primary orientation
    //   for (let i = 0; i < coordinates.length - 1; i++) {
    //     const p1 = coordinates[i];
    //     const p2 = coordinates[i + 1];

    //     const length = Math.sqrt((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2);

    //     if (length > maxLength) {
    //       maxLength = length;
    //       // Calculate angle in degrees
    //       angle = (Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180) / Math.PI;
    //     }
    //   }
    // }
    // return angle;

    // Method 2: Looks much aligned
    let maxDistance = 0;
    let dominantAngle = 0;
    if (coordinates) {
      for (let i = 0; i < coordinates.length - 1; i++) {
        const pt1 = point(coordinates[i]);
        const pt2 = point(coordinates[i + 1]);
        const dist = distance(pt1, pt2);

        if (dist > maxDistance) {
          maxDistance = dist;
          dominantAngle = bearing(pt1, pt2);
        }
      }
    }
    return dominantAngle;
  }

  // show area coverage for tagged spaces
  const areaCoverageLayer = useMemo(() => {
    const hasFloorPlan = floorPlan?.features && floorPlan.features.length > 0;
    if (!showTaggedSpaces || !hasFloorPlan || !allSpaces) return null;
    const floorPlanBearing = floorPlan && getFloorplanAngle(floorPlan);
    return getAreaCoverageLayer(sensorsById, allSpaces, floorPlan, floorPlanBearing ?? 0);
  }, [allSpaces, floorPlan, sensorsById, showTaggedSpaces]);

  // Used to add labels to the floorplan
  const labelLayer = useMemo(() => {
    if (!floorPlan) return null;
    return getLabelLayer(floorPlan, viewState.zoom);
  }, [floorPlan, viewState.zoom]);

  // Used to plot the users location on the map
  const targetPosLayer = useMemo(() => {
    if (!userPosition || userLocation !== currentLocation) return null;
    return getTargetPosLayer(userPosition);
  }, [userPosition, userLocation, currentLocation]);

  // Used to plot a circle around the user location on the map
  const perimeterPolygon = useMemo(() => {
    if (!userPosition || userLocation !== currentLocation) return null;
    return getSummaryCoverageLayer(userPosition);
  }, [userPosition, userLocation, currentLocation]);

  // Used to plot coloured area around sensors indicating coverage
  const sensorCoverageLayer = useMemo(() => {
    // no need to show coverage during space generation
    if (!floorPlan || !showSensorCoverage) return null;
    return getSensorCoverageLayer(activeMarker, markerData, floorPlan);
  }, [floorPlan, showSensorCoverage, activeMarker, markerData]);

  // Used for data with occupancy signatures to plot on Map measured positions of people
  const occPinLayer = useMemo(() => {
    if (activeMarker !== VarName.OccSignatures) return null;
    return getOccSignaturesLayer(markerData, dispatch);
  }, [activeMarker, dispatch, markerData]);

  const motionPinLayer = useMemo(() => {
    if (activeMarker !== VarName.MotionEvent) return null;
    const isUtlData = overrideData !== undefined;
    return getMotionPins(markerData, isUtlData);
  }, [activeMarker, markerData, overrideData]);

  if (areaCoverageLayer !== null) {
    deckGLLayers.push(areaCoverageLayer);
  }

  if (labelLayer !== null) {
    deckGLLayers.push(labelLayer);
  }

  if (sensorCoverageLayer !== null) {
    deckGLLayers.push(sensorCoverageLayer);
  }

  if (occPinLayer !== null) {
    deckGLLayers.push(occPinLayer);
  }

  if (motionPinLayer !== null) {
    deckGLLayers.push(motionPinLayer);
  }

  if (targetPosLayer !== null) {
    deckGLLayers.push(targetPosLayer);
  }

  if (perimeterPolygon !== null) {
    deckGLLayers.push(perimeterPolygon);
  }

  if (floorPlanLayer !== null) {
    deckGLLayers.push(floorPlanLayer);
  }

  return deckGLLayers;
}
